import { getAllActiveRoutesOptions, scheduleDriveMutation } from '@/api/main'
import { DriveBillingType, DriveType } from '@/api/main/types.gen'
import { invalidateSchedulerQueries } from '@/routes/scheduling.$startDate.$endDate.drive.$action.$id'
import { Modal, Stack, Button } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useQueryClient, useSuspenseQuery, useMutation } from '@tanstack/react-query'
import { useMemo } from 'react'
import { DriveFormValues, DriveFormModal } from './DriveFormModal'
import { useTranslation } from 'react-i18next'

function NewDriveModalContent({
  vehicleId,
  startDate,
  endDate,
  onClose,
}: {
  vehicleId: string
  startDate: string
  endDate: string
  onClose: () => void
}) {
  const { t } = useTranslation('scheduling')
  const queryClient = useQueryClient()

  const { data: routes } = useSuspenseQuery(getAllActiveRoutesOptions({}))

  const { mutate: createDrive, isPending } = useMutation(scheduleDriveMutation())

  const handleSubmit = (values: DriveFormValues) => {
    createDrive(
      {
        body: {
          route_id: values.route_id,
          route_version: values.route_version,
          drive_type: values.drive_type,
          billing_type: values.billing_type as DriveBillingType,
          vehicle_id: vehicleId,
          start_after: values.start_after,
          finish_before: values.finish_before,
        },
      },
      {
        onSuccess: () => {
          showNotification({ message: t('driveScheduledSuccessfully'), color: 'green' })
          invalidateSchedulerQueries(queryClient)
          onClose()
        },
        onError: () => {
          showNotification({ message: t('failedToScheduleDrive'), color: 'red' })
        },
      }
    )
  }

  const title = t('scheduleDrivesFor', { vehicleId: vehicleId.toUpperCase() })

  const initialValues = useMemo(
    () => ({
      start_after: decodeURIComponent(startDate),
      finish_before: decodeURIComponent(endDate),
      drive_type: ['statistical', 'evidence'] as Array<DriveType>,
      billing_type: 'billable' as DriveBillingType,
    }),
    [startDate, endDate]
  )

  return (
    <Modal opened={true} onClose={onClose} title={title} size="md">
      <DriveFormModal
        routes={routes}
        onSubmit={handleSubmit}
        isLoading={isPending}
        initialValues={initialValues}
        modalKey={`new-drive-form-${vehicleId}`}
      >
        <Stack gap="md" mt="md">
          <Button type="submit" form="drive-form" loading={isPending} fullWidth>
            {t('scheduleDrive')}
          </Button>
        </Stack>
      </DriveFormModal>
    </Modal>
  )
}

export default NewDriveModalContent
